import React from "react";
import { Controller, useFormContext, RegisterOptions } from "react-hook-form";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, TimePicker, DateTimePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";

dayjs.locale("es");

type PickerType = "date" | "time" | "datetime";

interface MDatePickerProps {
  name: string;
  pickerType: PickerType;
  label: string;
  validation?: RegisterOptions;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  disabled?: boolean;
  helperText?: string; // Nueva propiedad para el helper text
}

const MDatePicker: React.FC<MDatePickerProps> = ({
  name,
  pickerType,
  label,
  validation,
  minDate,
  maxDate,
  disabled,
  helperText, // Añadimos helperText a las props
}) => {
  const { control } = useFormContext();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Controller
        name={name}
        control={control}
        rules={{
          ...validation,
          validate: (value) => {
            const date = dayjs(value);
            if (!date.isValid() && value) {
              return "Fecha no válida";
            }
            if (minDate && date.isBefore(minDate, "day")) {
              return `La fecha debe ser posterior a ${minDate.format(
                "DD/MM/YYYY"
              )}`;
            }
            if (maxDate && date.isAfter(maxDate, "day")) {
              return `La fecha debe ser anterior a ${maxDate.format(
                "DD/MM/YYYY"
              )}`;
            }
            return true;
          },
        }}
        render={({ field, fieldState }) => {
          const commonProps = {
            ...field,
            label,
            disabled,
            minDate,
            maxDate,
            value: field.value ? dayjs(field.value) : null,
            onChange: (date: Dayjs | null) => {
              field.onChange(date ? date.format("YYYY-MM-DDTHH:mm:ss") : null);
            },
            slotProps: {
              textField: {
                error: !!fieldState.error,
                helperText: fieldState.error
                  ? fieldState.error.message
                  : helperText || "", // Mostrar helperText si no hay error
                fullWidth: true,
              },
            },
          };

          switch (pickerType) {
            case "date":
              return <DatePicker {...commonProps} />;
            case "time":
              return <TimePicker {...commonProps} />;
            case "datetime":
              return <DateTimePicker {...commonProps} />;
            default:
              return <div>No Picker Type Defined</div>;
          }
        }}
      />
    </LocalizationProvider>
  );
};

export default MDatePicker;
